/**
 * Persist draft large UPS rows created before the parent request is saved (#996 Phase 2).
 * Mirrors smallUpsConfigurationDraft.
 */

import { api } from './api';
import { toCreateLargeUpsPayload } from './largeUpsPayload';
import { networkService } from './network';
import { offlineCache } from './offlineCache';

import type { LargeUpsConfiguration } from '../types/largeUps';

export async function persistDraftLargeUpsConfigurations(
  requestId: string | number,
  units: LargeUpsConfiguration[],
): Promise<void> {
  const createdIds: number[] = [];
  const idKey = String(requestId);

  try {
    for (const unit of units) {
      const created = await api.post<LargeUpsConfiguration>(
        `/requests/${requestId}/large-ups`,
        toCreateLargeUpsPayload(unit),
      );
      if (created.id) {
        createdIds.push(created.id);
      }
    }
  } catch (error) {
    for (const largeUpsId of createdIds) {
      try {
        await api.delete(`/requests/${idKey}/large-ups/${largeUpsId}`);
      } catch (rollbackError) {
        console.error(
          'Failed to roll back large UPS after partial draft flush:',
          rollbackError,
        );
      }
    }
    throw error;
  }
}

/**
 * Flush draft large UPSs after Create Request save — online POST or offline cache + queue.
 */
export async function flushDraftLargeUpsConfigurations(
  requestId: string | number,
  units: LargeUpsConfiguration[],
  isOfflineRequest = false,
): Promise<void> {
  if (!units.length) {
    return;
  }

  const id = String(requestId);

  if (isOfflineRequest || !networkService.isConnected()) {
    const normalized = units.map((unit, index) => ({
      ...toCreateLargeUpsPayload(unit),
      id: unit.id && unit.id > 0 ? unit.id : -(Date.now() + index),
      requestId: typeof requestId === 'number' ? requestId : 0,
      displayOrder: unit.displayOrder ?? index + 1,
      status: unit.status ?? 'new',
    })) as LargeUpsConfiguration[];

    await offlineCache.updateCachedRequestLargeUps(id, normalized);
    for (const unit of normalized) {
      await offlineCache.addLargeUpsToOfflineQueue(
        id,
        'create',
        toCreateLargeUpsPayload(unit),
        unit.id,
      );
    }
    return;
  }

  await persistDraftLargeUpsConfigurations(requestId, units);
}
